import { Container } from '../ui/Container/Container';
import { useScrollReveal } from './useScrollReveal';
import { DemoFlow, dEdge, MobileDemoFlow } from './LandingFlowDemo';
import type { MobileNodeItem } from './LandingFlowDemo';
import type { Node, Edge } from '@xyflow/react';

const FEATURES = [
  'Live cursors & presence',
  'Share canvas with a link',
  'Duplicate into your workspace',
  '5 team seats included',
];

/* ═══════════════════════════════════════════════════════════════
   Collab canvas — nodes laid out in React Flow coordinates.
   fitView auto-scales everything to fit the container.
   ═══════════════════════════════════════════════════════════════ */
const NODES: Node[] = [
  {
    id: 'prompt',
    type: 'mini',
    position: { x: 0, y: 15 },
    data: {
      nodeType: 'prompt',
      body: 'Campaign hero visual for spring collection launch...',
      handleRight: true,
      width: 155,
    },
  },
  {
    id: 'media',
    type: 'mini',
    position: { x: 15, y: 135 },
    data: {
      nodeType: 'media',
      imageSrc: '/images/lp/collab-clothes.webp',
      filename: 'brand-board.png',
      handleRight: true,
      width: 145,
    },
  },
  {
    id: 'generate',
    type: 'mini',
    position: { x: 400, y: 40 },
    data: {
      nodeType: 'generate',
      badge: 'Inline',
      handleLeft: true,
      handleRight: true,
      imageSrc: '/images/lp/collab-group.webp',
      footerLines: ['Nano Banana Pro · 1:1', 'Regenerate'],
      width: 160,
    },
  },
  {
    id: 'output',
    type: 'mini',
    position: { x: 760, y: 0 },
    data: {
      nodeType: 'output',
      handleLeft: true,
      imageSrc: '/images/lp/collab-male.webp',
      width: 140,
    },
  },
  {
    id: 'variation',
    type: 'mini',
    position: { x: 780, y: 155 },
    data: {
      nodeType: 'variation',
      handleLeft: true,
      imageSrc: '/images/lp/collab-women.webp',
      width: 130,
    },
  },
];

const EDGES: Edge[] = [
  dEdge('e1', 'prompt', 'generate', '#7ea8c9'),
  dEdge('e2', 'media', 'generate', '#9678b4'),
  dEdge('e3', 'generate', 'output', '#7b6ba0'),
  dEdge('e4', 'generate', 'variation', '#7b6ba0'),
];

/* ── Mobile-only static node list ───────────────────────────── */
const COLLAB_MOBILE: MobileNodeItem[] = [
  { nodeType: 'prompt', body: 'Campaign hero visual for spring collection launch...' },
  { nodeType: 'media', imageSrc: '/images/lp/collab-clothes.webp', filename: 'brand-board.png', edgeColor: '#9678b4' },
  { nodeType: 'generate', badge: 'Inline', imageSrc: '/images/lp/collab-group.webp', footerLines: ['Nano Banana Pro · 1:1', 'Regenerate'], edgeColor: '#9678b4' },
  { nodeType: 'output', imageSrc: '/images/lp/collab-male.webp', edgeColor: '#7b6ba0' },
];

export function LandingCollab() {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-12 md:py-20 bg-[#f5f7fa]" id="collab">
      <Container size="lg" className="max-w-[1100px]">
        <div ref={ref} className="lp-reveal text-center">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-primary-500 mb-3">
            Collaboration
          </p>
          <h2
            className="text-[clamp(1.75rem,4vw,2.6rem)] font-semibold leading-[1.15] tracking-tight mb-3"
            style={{ color: '#1d2939' }}
          >
            Create together,
            <br />
            on the same canvas.
          </h2>
          <p className="text-[clamp(0.95rem,2vw,1.1rem)] text-[#5a6478] max-w-[540px] mx-auto leading-relaxed">
            Multiple people editing the same canvas in real time. See cursors, share outputs, iterate as a team.
          </p>
        </div>

        {/* Desktop: Canvas demo with React Flow */}
        <div className="relative mt-8 hidden md:block">
          <DemoFlow
            nodes={NODES}
            edges={EDGES}
            className="rounded-[20px] border border-black/[0.06]"
            style={{ height: 340 }}
            bgGap={18}
            fitPadding={0.12}
          />

          {/* Presence avatars — overlaid on top */}
          <div className="absolute top-3 right-3 flex z-[10]">
            <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white bg-[#9678b4]">R</div>
            <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white bg-[#5b8ab5] -ml-[5px]">A</div>
            <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white bg-[#a8b44a] -ml-[5px]">M</div>
          </div>

          {/* Animated cursors */}
          <div className="absolute z-[10] pointer-events-none" style={{ animation: 'lpCursor1 8s ease-in-out infinite' }}>
            <svg className="w-5 h-5" viewBox="0 0 16 16" fill="#9678b4"><path d="M0 0l6 14 2-6 6-2z" /></svg>
            <span className="text-[10px] leading-none font-semibold px-[6px] py-[4px] rounded-[4px] text-white ml-2.5 -mt-[3px] whitespace-nowrap inline-block" style={{ background: '#9678b4' }}>Rohit</span>
          </div>
          <div className="absolute z-[10] pointer-events-none" style={{ animation: 'lpCursor2 10s ease-in-out infinite' }}>
            <svg className="w-5 h-5" viewBox="0 0 16 16" fill="#5b8ab5"><path d="M0 0l6 14 2-6 6-2z" /></svg>
            <span className="text-[10px] leading-none font-semibold px-[6px] py-[4px] rounded-[4px] text-white ml-2.5 -mt-[3px] whitespace-nowrap inline-block" style={{ background: '#5b8ab5' }}>Anna</span>
          </div>
          <div className="absolute z-[10] pointer-events-none" style={{ animation: 'lpCursor3 7s ease-in-out infinite' }}>
            <svg className="w-5 h-5" viewBox="0 0 16 16" fill="#a8b44a"><path d="M0 0l6 14 2-6 6-2z" /></svg>
            <span className="text-[10px] leading-none font-semibold px-[6px] py-[4px] rounded-[4px] text-white ml-2.5 -mt-[3px] whitespace-nowrap inline-block" style={{ background: '#a8b44a' }}>Marco</span>
          </div>
        </div>

        {/* Mobile: static card stack with presence avatars + cursors */}
        <div className="relative mt-8 md:hidden max-w-[320px] mx-auto">
          {/* Presence avatars */}
          <div className="absolute top-3 right-3 flex z-[10]">
            <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white bg-[#9678b4]">R</div>
            <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white bg-[#5b8ab5] -ml-[5px]">A</div>
            <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white bg-[#a8b44a] -ml-[5px]">M</div>
          </div>
          {/* Animated cursors */}
          <div className="absolute z-[10] pointer-events-none" style={{ animation: 'lpCursor1 8s ease-in-out infinite' }}>
            <svg className="w-5 h-5" viewBox="0 0 16 16" fill="#9678b4"><path d="M0 0l6 14 2-6 6-2z" /></svg>
            <span className="text-[10px] leading-none font-semibold px-[6px] py-[4px] rounded-[4px] text-white ml-2.5 -mt-[3px] whitespace-nowrap inline-block" style={{ background: '#9678b4' }}>Rohit</span>
          </div>
          <div className="absolute z-[10] pointer-events-none" style={{ animation: 'lpCursor2 10s ease-in-out infinite' }}>
            <svg className="w-5 h-5" viewBox="0 0 16 16" fill="#5b8ab5"><path d="M0 0l6 14 2-6 6-2z" /></svg>
            <span className="text-[10px] leading-none font-semibold px-[6px] py-[4px] rounded-[4px] text-white ml-2.5 -mt-[3px] whitespace-nowrap inline-block" style={{ background: '#5b8ab5' }}>Anna</span>
          </div>
          <MobileDemoFlow items={COLLAB_MOBILE} className="rounded-[20px]" />
        </div>

        {/* Feature bullets */}
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 justify-center">
          {FEATURES.map((f) => (
            <div key={f} className="flex items-center gap-1.5 text-[0.78rem] text-[#5a6478]">
              <span className="w-[5px] h-[5px] rounded-full bg-primary-500" />
              {f}
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
